/* ============================================================
   LUMINA — THEME.JS
   Cart Drawer, Mobile Menu, Product Form, Scroll FX
   ============================================================ */

'use strict';

/* ——— UTILS ——— */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
const formatMoney = (cents, currency = window.Shopify?.currency?.active || 'BRL') => {
  const amount = (cents / 100).toFixed(2);
  const formatted = amount.replace('.', ',').replace(/(\d)(?=(\d{3})+,)/g, '$1.');
  return `R$\xa0${formatted}`;
};

const debounce = (fn, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), delay);
  };
};

/* ——— CART API ——— */
const CartAPI = {
  async getCart() {
    const url = window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart.js' : '/cart.js';
    const res = await fetch(url, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
    return res.json();
  },
  async addItem(variantId, quantity = 1, properties = {}) {
    console.log('CartAPI.addItem called with:', { variantId, quantity, properties });
    const url = window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js';
    console.log('CartAPI.addItem URL:', url);
    const payload = {
      items: [{ id: parseInt(variantId, 10), quantity: parseInt(quantity, 10) || 1, properties }]
    };
    console.log('CartAPI.addItem payload:', payload);
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    console.log('CartAPI.addItem response:', { status: res.status, ok: res.ok, data });
    if (!res.ok) throw new Error(data.description || 'Erro ao adicionar');
    return data;
  },
  async updateItem(key, quantity) {
    const url = window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart/change.js' : '/cart/change.js';
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ id: key, quantity }),
    });
    return res.json();
  },
  async removeItem(key) {
    return CartAPI.updateItem(key, 0);
  },
};

/* ——— TOAST ——— */
function showToast(message, type = 'default', duration = 3500) {
  const el = document.createElement('div');
  el.className = `toast ${type}`;
  el.textContent = message;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), duration);
}

/* ——— CART DRAWER ——— */
class CartDrawer {
  constructor() {
    this.drawer = $('#cart-drawer');
    this.overlay = $('#cart-overlay');
    if (!this.drawer) return;

    this.body = this.drawer.querySelector('.cart-drawer__body');
    this.countEl = $$('.cart-count');
    this.productFeaturedImageCache = new Map();

    this._bindEvents();
  }

  _bindEvents() {
    // Open triggers
    $$(
      '[data-cart-drawer-open], .header-action-btn[href*="cart"]'
    ).forEach((el) => {
      el.addEventListener('click', (e) => {
        // Se for um link e não tiver o atributo de abrir o drawer explicitamente, 
        // mas estiver no header, nós abrimos o drawer.
        e.preventDefault();
        this.open();
      });
    });

    // Close button
    this.drawer.querySelector('.cart-drawer__close')?.addEventListener('click', () => this.close());

    // Overlay click
    this.overlay?.addEventListener('click', () => this.close());

    // ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) this.close();
    });

    // Listen for cart updates - Debounced to handle burst updates
    this._debouncedRender = debounce(() => this.renderItems(), 50);
    document.addEventListener('lumina:cart-updated', () => this._debouncedRender());
    document.addEventListener('lumina:open-cart', () => this.open());
  }

  open() {
    // Re-busca elementos se necessário (mobile pode não ter encontrado no boot)
    if (!this.drawer) this.drawer = document.getElementById('cart-drawer');
    if (!this.overlay) this.overlay = document.getElementById('cart-overlay');
    if (!this.drawer) { window.location.href = '/cart'; return; }
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.overlay?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this._debouncedRender();
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.overlay?.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  async renderItems() {
    try {
      this.drawer.classList.add('is-loading');
      const cart = await CartAPI.getCart();
      this._updateCount(cart.item_count);
      this._updateShippingBar(cart.total_price);

      if (cart.item_count === 0) {
        this.body.innerHTML = this._emptyCartHTML();
        const footer = this.drawer.querySelector('.cart-drawer__footer');
        if (footer) footer.innerHTML = '';
        this.drawer.classList.remove('is-loading');
        return;
      }

      this.body.innerHTML = cart.items.map((item) => this._itemHTML(item)).join('');
      this._bindItemEvents();
      this._renderFooter(cart);
      this._syncFeaturedImages(cart.items);

      this.drawer.classList.remove('is-loading');
    } catch (err) {
      if (err.status !== 429) {
        console.error('Cart render error:', err);
      }
      this.drawer.classList.remove('is-loading');
    }
  }

  // Alias compatible with old scripts calling window.CartDrawer.refresh()
  refresh() {
    this._debouncedRender();
  }

  _updateCount(count) {
    // Header badge (cestinha)
    this.countEl.forEach((el) => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
      if (el.id === 'cart-count-badge') {
        el.style.display = count > 0 ? 'block' : 'none';
      }
    });

    // Drawer header count (entre parênteses)
    const headerCount = this.drawer.querySelector('.cart-count-header');
    if (headerCount) {
      headerCount.textContent = count > 0 ? `(${count})` : '';
    }
  }

  _renderFooter(cart) {
    const footer = this.drawer.querySelector('.cart-drawer__footer');
    if (!footer) return;

    let discountsHTML = '';
    if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
      discountsHTML = `
        <div class="cart-discounts">
          ${cart.cart_level_discount_applications.map(d => `
            <div class="cart-discount-row">
              <span class="cart-discount-row__label">${d.title}</span>
              <span class="cart-discount-row__amount">−${formatMoney(d.total_allocated_amount)}</span>
            </div>
          `).join('')}
        </div>`;
    }

    footer.innerHTML = `
      ${discountsHTML}
      <div class="cart-subtotal">
        <span class="cart-subtotal__label">Subtotal</span>
        <span class="cart-subtotal__price">${formatMoney(cart.total_price)}</span>
      </div>
      <p class="cart-subtotal__note">Frete e impostos calculados no checkout</p>
      <form action="/checkout" method="post" class="cart-drawer__checkout-form">
        <button type="submit" name="checkout" class="cart-drawer__checkout btn btn-primary btn-full">
          Finalizar compra 
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </button>
      </form>
      <a href="/cart" class="cart-drawer__continue">Ver carrinho completo</a>
    `;
  }

  _updateCount(count) {
    this.countEl.forEach((el) => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  _updateShippingBar(totalCents) {
    const threshold = 29900; // R$299,00
    const bar = this.drawer.querySelector('.shipping-bar__fill');
    const text = this.drawer.querySelector('.shipping-bar__text');
    if (!bar) return;
    const pct = Math.min((totalCents / threshold) * 100, 100);
    bar.style.width = `${pct}%`;
    if (text) {
      const remaining = threshold - totalCents;
      text.innerHTML =
        remaining > 0
          ? `Faltam <strong>${formatMoney(remaining)}</strong> para frete grátis 🚚`
          : '<strong>🎉 Você ganhou frete grátis!</strong>';
    }
  }

  _itemHTML(item) {
    const imgSrc = item.image ? item.image : 'https://via.placeholder.com/80x100';

    const discountsHTML = (item.line_level_discount_allocations || []).map(d => `
      <span class="cart-item__discount-badge">
        ${d.discount_application.title} (−${formatMoney(d.amount)})
      </span>
    `).join('');

    const priceHTML = item.original_line_price !== item.final_line_price
      ? `<span class="cart-item__original-price">${formatMoney(item.original_line_price)}</span>
         <span class="cart-item__price">${formatMoney(item.final_line_price)}</span>`
      : `<span class="cart-item__price">${formatMoney(item.final_line_price)}</span>`;

    const variantHTML = item.variant_title && item.variant_title !== 'Default Title'
      ? `<div class="cart-item__variant">${item.variant_title}</div>`
      : '';

    return `
      <div class="cart-item" data-key="${item.key}">
        <a href="${item.url}" class="cart-item__image-wrap" tabindex="-1" aria-hidden="true">
          <img class="cart-item__image" src="${imgSrc}" alt="${item.title}" width="54" height="68" loading="lazy">
        </a>
        <div class="cart-item__details">
          <div class="cart-item__top">
            <div class="cart-item__meta">
              <a href="${item.url}" class="cart-item__name">${item.product_title}</a>
              ${variantHTML}
            </div>
            <button class="cart-item__remove" data-key="${item.key}" aria-label="Remover item">Remover</button>
          </div>
          <div class="cart-item__pricing">
            <div class="cart-item__price-wrap">
              ${priceHTML}
            </div>
            ${discountsHTML}
          </div>
          <div class="cart-item__footer">
            <div class="qty-control" data-key="${item.key}">
              <button class="qty-btn qty-btn--minus" aria-label="Diminuir">−</button>
              <input class="qty-input" type="number" value="${item.quantity}" min="0" max="99" aria-label="Quantidade">
              <button class="qty-btn qty-btn--plus" aria-label="Aumentar">+</button>
            </div>
            <div class="cart-item__line-total">${formatMoney(item.final_line_price)}</div>
          </div>
        </div>
      </div>`;
  }

  async _syncFeaturedImages(items) {
    await Promise.all(items.map((item) => this._applyFeaturedImage(item)));
  }

  async _applyFeaturedImage(item) {
    const handle = item.handle || this._getProductHandleFromUrl(item.url);
    if (!handle) return;

    const featuredImage = await this._getProductFeaturedImage(handle);
    if (!featuredImage) return;

    const itemEl = [...this.body.querySelectorAll('.cart-item')].find((el) => el.dataset.key === item.key);
    const imageEl = itemEl?.querySelector('.cart-item__image');

    if (imageEl) {
      imageEl.src = featuredImage;
    }
  }

  _getProductHandleFromUrl(url) {
    if (!url) return null;
    const match = url.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  async _getProductFeaturedImage(handle) {
    if (this.productFeaturedImageCache.has(handle)) {
      return this.productFeaturedImageCache.get(handle);
    }

    try {
      const root = window.Shopify?.routes?.root || '/';
      const res = await fetch(`${root}products/${handle}.js`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!res.ok) {
        throw new Error(`Failed to load product ${handle}`);
      }

      const product = await res.json();
      const featuredImage = product?.images?.[0] || product?.featured_image || null;
      this.productFeaturedImageCache.set(handle, featuredImage);
      return featuredImage;
    } catch (error) {
      console.warn('Cart featured image fallback failed:', handle, error);
      this.productFeaturedImageCache.set(handle, null);
      return null;
    }
  }

  _emptyCartHTML() {
    return `
      <div class="cart-empty animate-fade-in">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
          <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/>
          <path d="M16 10a4 4 0 01-8 0"/>
        </svg>
        <div class="cart-empty__title">Carrinho vazio</div>
        <p class="cart-empty__text">Adicione peças incríveis ao seu carrinho</p>
        <a href="/collections/all" class="btn btn-primary">Ver coleção</a>
      </div>`;
  }

  _bindItemEvents() {
    this.body.querySelectorAll('.cart-item').forEach((item) => {
      const key = item.dataset.key;
      const qtyInput = item.querySelector('.qty-input');
      const minus = item.querySelector('.qty-btn--minus');
      const plus = item.querySelector('.qty-btn--plus');
      const remove = item.querySelector('.cart-item__remove');

      // Debounce quantity updates (400ms) to prevent rate limiting 429
      const updateQtyDebounced = debounce(async (newQty) => {
        try {
          await CartAPI.updateItem(key, newQty);
          this._debouncedRender();
        } catch (err) {
          console.error('Quantity update error:', err);
          this._debouncedRender();
        }
      }, 400);

      const handleQtyChange = (delta) => {
        const current = parseInt(qtyInput.value, 10) || 0;
        const target = Math.max(0, current + delta);
        qtyInput.value = target; // Immediate UI update for the input field
        item.style.opacity = '.5';
        updateQtyDebounced(target);
      };

      minus?.addEventListener('click', () => handleQtyChange(-1));
      plus?.addEventListener('click', () => handleQtyChange(1));

      qtyInput?.addEventListener('change', () => {
        const target = Math.max(0, parseInt(qtyInput.value, 10) || 0);
        item.style.opacity = '.5';
        updateQtyDebounced(target);
      });

      remove?.addEventListener('click', () => {
        item.style.opacity = '.5';
        CartAPI.removeItem(key).then(() => this._debouncedRender());
      });
    });
  }
}

/* ——— PRODUCT FORM ——— */
class ProductForm {
  constructor(form) {
    this.form = form;
    this.btn = form.querySelector('[data-atc-btn]');
    this.variantInput = form.querySelector('input[name="id"]');
    this._bindEvents();
  }

  _bindEvents() {
    this.form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const variantId = this.variantInput?.value;
      if (!variantId) return;
      const qtyInput = this.form.querySelector('input[name="quantity"]');
      const quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      await this._addToCart(variantId, quantity);
    });
  }

  async _addToCart(variantId, quantity = 1) {
    console.log('ProductForm._addToCart called with:', { variantId, quantity });
    this.btn?.classList.add('loading');
    this.btn && (this.btn.disabled = true);
    try {
      console.log('Calling CartAPI.addItem...');
      await CartAPI.addItem(variantId, quantity);
      console.log('CartAPI.addItem success');
      document.dispatchEvent(new CustomEvent('lumina:cart-updated'));
      document.dispatchEvent(new CustomEvent('lumina:open-cart'));
      showToast('Produto adicionado ao carrinho! 🛒', 'success');
    } catch (err) {
      console.error('CartAPI.addItem error:', err);
      showToast(err.message || 'Erro ao adicionar ao carrinho.', 'error');
    } finally {
      this.btn?.classList.remove('loading');
      this.btn && (this.btn.disabled = false);
    }
  }
}

/* ——— VARIANT SELECTS ——— */
class VariantSelects {
  constructor(container) {
    this.container = container;
    this.productData = JSON.parse(
      container.querySelector('[data-product-json]')?.textContent || '{}'
    );
    this._bindEvents();
    this._updateUI();
  }

  _getSelectedValues() {
    return $$('[data-option]', this.container).map((el) => el.dataset.value);
  }

  _findVariant(values) {
    return (this.productData.variants || []).find((v) =>
      v.options.every((o, i) => o === values[i])
    );
  }

  _bindEvents() {
    $$('[data-option]', this.container).forEach((btn) => {
      btn.addEventListener('click', () => {
        const name = btn.dataset.option;
        $$(`[data-option="${name}"]`, this.container).forEach((b) =>
          b.classList.remove('selected')
        );
        btn.classList.add('selected');
        this._updateUI();
      });
    });
  }

  _updateUI() {
    const values = this._getSelectedValues();
    const variant = this._findVariant(values);
    const priceEl = this.container.querySelector('[data-price]');
    const compareEl = this.container.querySelector('[data-compare-price]');
    const variantInput = this.container.querySelector('input[name="id"]');
    const atcBtn = this.container.querySelector('[data-atc-btn]');

    if (!variant) return;

    if (variantInput) variantInput.value = variant.id;

    if (priceEl) priceEl.textContent = formatMoney(variant.price);
    if (compareEl) {
      compareEl.textContent = variant.compare_at_price ? formatMoney(variant.compare_at_price) : '';
      compareEl.style.display = variant.compare_at_price ? 'inline' : 'none';
    }

    if (atcBtn) {
      atcBtn.disabled = !variant.available;
      const tAvail = atcBtn.dataset.textAvailable || 'Adicionar ao carrinho';
      const tUnavail = atcBtn.dataset.textUnavailable || 'Esgotado';
      const atcText = variant.available ? tAvail : tUnavail;
      const textSpan = atcBtn.querySelector('.atc-btn-text');
      if (textSpan) {
        textSpan.textContent = atcText;
      } else {
        atcBtn.textContent = atcText;
      }
    }

    // Update URL
    if (window.history.replaceState) {
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }
}

/* ——— MOBILE MENU ——— */
class MobileMenu {
  constructor() {
    this.nav = $('#mobile-nav');
    if (!this.nav) return;
    this.overlay = $('#cart-drawer-overlay'); // reuse overlay or create dedicated

    $$('[data-mobile-menu-open]').forEach((btn) => {
      btn.addEventListener('click', () => this.open());
    });

    this.nav.querySelector('[data-mobile-menu-close]')?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.nav.classList.contains('is-open')) this.close();
    });
  }

  open() {
    this.nav.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.nav.classList.remove('is-open');
    document.body.style.overflow = '';
  }
}

/* ——— HEADER SCROLL FX ——— */
function initHeaderScroll() {
  const header = $('.site-header');
  if (!header) return;

  const bar = document.querySelector('.announcement-bar');

  // Ajusta o top do header dinamicamente conforme o scroll
  // Quando a announcement bar ainda está visível, header fica abaixo dela.
  // Quando a barra sai da tela, header sobe para top: 0.
  function updateHeaderTop() {
    if (!bar || bar.classList.contains('announcement-bar--hidden')) {
      header.style.top = '0px';
      return;
    }
    const bottomOfBar = bar.getBoundingClientRect().bottom;
    header.style.top = Math.max(0, bottomOfBar) + 'px';
  }

  updateHeaderTop();
  window.addEventListener('scroll', updateHeaderTop, { passive: true });

  // Recalcula se a announcement bar for fechada
  const barClose = document.querySelector('.announcement-bar__close');
  if (barClose) {
    barClose.addEventListener('click', function () {
      setTimeout(updateHeaderTop, 50);
    });
  }

  // Só aplica o comportamento de transparência → sólido na homepage
  if (!header.classList.contains('site-header--transparent')) return;

  const sentinel = document.createElement('div');
  sentinel.style.cssText = 'position:absolute;top:' + (header.offsetHeight || 80) + 'px;height:1px;width:1px;pointer-events:none;';
  document.body.prepend(sentinel);

  const observer = new IntersectionObserver(
    ([entry]) => {
      const scrolled = !entry.isIntersecting;
      header.classList.toggle('scrolled', scrolled);
      header.classList.toggle('site-header--scrolled', scrolled);
    },
    { threshold: 0 }
  );
  observer.observe(sentinel);
}

/* ——— SCROLL REVEAL ——— */
function initScrollReveal() {
  const els = $$('[data-reveal]:not(.revealed)');
  if (!els.length) return;
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('revealed');
          observer.unobserve(e.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
  );
  els.forEach((el) => observer.observe(el));
}
window.initScrollReveal = initScrollReveal;

/* ——— PRODUCT GALLERY ——— */
function initProductGallery() {
  $$('.product-gallery').forEach((gallery) => {
    const main = gallery.querySelector('.product-gallery__main img');
    const thumbs = $$('.product-gallery__thumb', gallery);
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const src = thumb.querySelector('img').src
          .replace('_100x.', '_800x.')
          .replace('_160x.', '_800x.');
        if (main) main.src = src;
        thumbs.forEach((t) => t.classList.remove('active'));
        thumb.classList.add('active');
      });
    });
  });
}

/* ——— QUICK ADD ——— */
function initQuickAdd() {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    btn.classList.add('loading');
    try {
      await CartAPI.addItem(variantId);
      document.dispatchEvent(new CustomEvent('lumina:cart-updated'));
      document.dispatchEvent(new CustomEvent('lumina:open-cart'));
      showToast('Produto adicionado! 🛒', 'success');
    } catch {
      showToast('Selecione uma variante primeiro.', 'error');
    } finally {
      btn.classList.remove('loading');
    }
  });
}

/* ——— OPEN CART EVENT ——— */
function initCartOpenEvent(cartDrawer) {
  document.addEventListener('lumina:open-cart', () => cartDrawer?.open());
  // Intercept cart icon link
  $$('a[href="/cart"], .header-action-btn').forEach((link) => {
    if (link.href?.includes('/cart') || link.dataset.cartDrawerOpen !== undefined) {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        cartDrawer?.open();
      });
    }
  });
}

/* ——— HERO SLIDER ——— */
class HeroSlider {
  constructor(el) {
    this.el = el;
    this.track = el.querySelector('[data-slider-track]');
    this.slides = el.querySelectorAll('.hero-banner__slide');
    this.btnPrev = el.querySelector('[data-slider-prev]');
    this.btnNext = el.querySelector('[data-slider-next]');
    this.progressBar = el.querySelector('[data-slider-progress]');
    this.currentIndex = 0;
    this.timer = null;
    this.progressInterval = null;
    this.autoplayTime = 6000; // 6 secundos

    if (!this.track || this.slides.length < 2) return;

    this.bindEvents();
    this.initDragAndDrop();
    this.updateProgress();
    this.startAutoplay();
  }

  bindEvents() {
    this.btnPrev?.addEventListener('click', () => { this.navigate(-1); this.resetAutoplay(); });
    this.btnNext?.addEventListener('click', () => { this.navigate(1); this.resetAutoplay(); });

    if (this.progressBar) {
      this.progressBar.parentElement.addEventListener('click', (e) => {
        const rect = this.progressBar.parentElement.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const percentage = clickX / rect.width;
        const targetIndex = Math.floor(percentage * this.slides.length);
        this.goTo(Math.min(Math.max(0, targetIndex), this.slides.length - 1));
        this.resetAutoplay();
      });
    }

    this.track.addEventListener('scroll', () => {
      if (this.el.dataset.transition === 'fade') return;
      const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
      if (index !== this.currentIndex) {
        this.currentIndex = index;
        this.updateProgress();
      }
    }, { passive: true });

    this.el.addEventListener('mouseenter', () => clearInterval(this.timer));
    this.el.addEventListener('mouseleave', () => this.startAutoplay());
  }

  initDragAndDrop() {
    if (this.el.dataset.transition === 'fade') return;
    this.isDown = false;
    this.isDragged = false;
    this.startX = 0;
    this.scrollLeftState = 0;

    this.track.querySelectorAll('img, a, video').forEach(el => {
      el.addEventListener('dragstart', (e) => e.preventDefault());
    });

    this.track.addEventListener('click', (e) => {
      if (this.isDragged) e.preventDefault();
    });

    this.track.addEventListener('mousedown', (e) => {
      this.isDown = true;
      this.isDragged = false;
      this.track.style.cursor = 'grabbing';
      this.track.style.scrollSnapType = 'none';
      this.track.style.scrollBehavior = 'auto';
      this.startX = e.pageX - this.track.offsetLeft;
      this.scrollLeftState = this.track.scrollLeft;
      this.pauseAutoplay();
    });

    const stopDrag = () => {
      this.isDown = false;
      this.track.style.cursor = 'grab';
      this.track.style.scrollSnapType = 'x mandatory';
      this.track.style.scrollBehavior = 'smooth';
      setTimeout(() => this.isDragged = false, 50);
      this.startAutoplay();
    };

    this.track.addEventListener('mouseleave', stopDrag);
    this.track.addEventListener('mouseup', stopDrag);

    this.track.addEventListener('mousemove', (e) => {
      if (!this.isDown) return;
      e.preventDefault();
      const x = e.pageX - this.track.offsetLeft;
      const walk = (x - this.startX) * 1.5;

      if (Math.abs(walk) > 10) this.isDragged = true;
      this.track.scrollLeft = this.scrollLeftState - walk;
    });
  }

  navigate(dir) {
    let target = this.currentIndex + dir;
    if (target < 0) target = this.slides.length - 1;
    if (target >= this.slides.length) target = 0;
    this.goTo(target);
  }

  goTo(index) {
    this.currentIndex = index;
    const slide = this.slides[index];
    if (slide) {
      if (this.el.dataset.transition !== 'fade') {
        this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      }
    }
    this.updateProgress();
  }

  updateProgress() {
    this.slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === this.currentIndex);
    });
  }

  startAutoplay() {
    this.pauseAutoplay();
    let startTime = Date.now();

    const animateProgress = () => {
      const elapsed = Date.now() - startTime;
      let percentage = (elapsed / this.autoplayTime) * 100;

      if (percentage >= 100) {
        percentage = 100;
        this.pauseAutoplay();
        this.navigate(1);
        return; // wait for navigate to restart it organically
      }

      if (this.progressBar) {
        this.progressBar.style.width = `${percentage}%`;
      }

      this.progressInterval = requestAnimationFrame(animateProgress);
    };

    this.progressInterval = requestAnimationFrame(animateProgress);
  }

  pauseAutoplay() {
    if (this.progressInterval) cancelAnimationFrame(this.progressInterval);
  }

  resetAutoplay() {
    this.pauseAutoplay();
    if (this.progressBar) {
      this.progressBar.style.transition = 'none';
      this.progressBar.style.width = '0%';
      // Força reflow p/ remover a transição antes de reiniciar
      void this.progressBar.offsetWidth;
      this.progressBar.style.transition = 'width 0.1s linear';
    }
    this.startAutoplay();
  }
}

/* ——— COLLECTION SLIDER SCROLL ——— */
class CollectionSlider {
  constructor(el) {
    this.section = el;
    this.track = el.querySelector('[data-slider-track]');
    this.btnPrev = el.querySelector('.slider-prev');
    this.btnNext = el.querySelector('.slider-next');

    // Drag to scroll variables
    this.isDown = false;
    this.startX = 0;
    this.scrollLeftState = 0;

    if (!this.track) return;
    this._bindEvents();
    this._initDragAndDrop();
    this._updateButtons();
  }

  _bindEvents() {
    this.btnPrev?.addEventListener('click', () => this._scroll(-1));
    this.btnNext?.addEventListener('click', () => this._scroll(1));
    this.track.addEventListener('scroll', () => this._updateButtons(), { passive: true });
  }

  _initDragAndDrop() {
    this.isDragged = false; // Add variable to distinguish click vs drag

    // Prevent default drag ghost on images/links
    this.track.querySelectorAll('img, a').forEach(el => {
      el.addEventListener('dragstart', (e) => e.preventDefault());
    });

    // Prevent click jumping to product page if user was dragging
    this.track.addEventListener('click', (e) => {
      if (this.isDragged) {
        e.preventDefault();
      }
    });

    this.track.addEventListener('mousedown', (e) => {
      this.isDown = true;
      this.isDragged = false;
      this.track.style.cursor = 'grabbing';
      this.track.style.scrollSnapType = 'none'; // disables snap on drag
      this.track.style.scrollBehavior = 'auto'; // disables smooth jump
      this.startX = e.pageX - this.track.offsetLeft;
      this.scrollLeftState = this.track.scrollLeft;
    });

    this.track.addEventListener('mouseleave', () => {
      this.isDown = false;
      this.track.style.cursor = 'grab';
      this.track.style.scrollSnapType = 'x mandatory';
      this.track.style.scrollBehavior = 'smooth';
      setTimeout(() => this.isDragged = false, 50);
    });

    this.track.addEventListener('mouseup', () => {
      this.isDown = false;
      this.track.style.cursor = 'grab';
      this.track.style.scrollSnapType = 'x mandatory';
      this.track.style.scrollBehavior = 'smooth';
      setTimeout(() => this.isDragged = false, 50); // Small timeout to catch the click event
    });

    this.track.addEventListener('mousemove', (e) => {
      if (!this.isDown) return;
      e.preventDefault();
      const x = e.pageX - this.track.offsetLeft;

      const walk = (x - this.startX) * 1.5;

      // If we moved the mouse consistently, consider it a drag
      if (Math.abs(walk) > 10) {
        this.isDragged = true;
      }

      this.track.scrollLeft = this.scrollLeftState - walk;
    });
  }

  _scroll(dir) {
    // Scroll width of one item + gap (approx 320px + 24px)
    // Or just use clientWidth of the first slide
    const slide = this.track.querySelector('.collection-slider__slide');
    const scrollAmount = slide ? slide.offsetWidth + 24 : 344;
    this.track.scrollBy({ left: scrollAmount * dir, behavior: 'smooth' });
  }

  _updateButtons() {
    if (!this.btnPrev || !this.btnNext) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    this.btnPrev.disabled = scrollLeft <= 10;
    this.btnNext.disabled = scrollLeft >= scrollWidth - clientWidth - 10;
  }
}

/* ——— FLOATING VIDEO WIDGET (REELS) ——— */

// Load YT Iframe API globally
let isYtApiLoaded = false;
let ytApiPromise = null;

function loadYtApi() {
  if (isYtApiLoaded) return Promise.resolve();
  if (ytApiPromise) return ytApiPromise;

  ytApiPromise = new Promise((resolve) => {
    window.onYouTubeIframeAPIReady = () => {
      isYtApiLoaded = true;
      resolve();
    };
    const tag = document.createElement('script');
    tag.src = "https://www.youtube.com/iframe_api";
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
  });
  return ytApiPromise;
}

class FloatingVideo {
  constructor(section) {
    this.section = section;
    this.opener = section.querySelector('[data-floating-opener]');
    this.modal = section.querySelector('[data-story-modal]');
    this.dataScript = section.querySelector('[data-story-data]');

    if (!this.opener || !this.modal || !this.dataScript) return;

    try {
      this.stories = JSON.parse(this.dataScript.textContent);
    } catch (e) {
      console.error('Lumina: Erro ao parsear dados dos stories', e);
      return;
    }

    if (this.stories.length === 0) return;

    // UI Elements
    this.closeBtns = this.modal.querySelectorAll('[data-modal-close]');
    this.btnsPrev = this.modal.querySelectorAll('[data-story-prev]');
    this.btnsNext = this.modal.querySelectorAll('[data-story-next]');

    this.iframeContainerId = this.modal.querySelector('.story-modal__iframe').id;
    this.loader = this.modal.querySelector('[data-story-loader]');
    this.poster = this.modal.querySelector('[data-story-poster]');
    this.uiOverlay = this.modal.querySelector('[data-story-ui]');
    this.titleEl = this.modal.querySelector('[data-story-title]');
    this.descEl = this.modal.querySelector('[data-story-desc]');

    this.actionContainer = this.modal.querySelector('[data-story-action]');
    this.actionLink = this.modal.querySelector('[data-story-action-link]');
    this.actionText = this.modal.querySelector('[data-story-action-text]');

    this.progressBars = Array.from(this.modal.querySelectorAll('[data-progress-fill]'));

    this.currentIndex = 0;
    this.player = null;
    this.progressReq = null;
    this.isPlaying = false;

    this._bindEvents();
    this._initScrollBehavior();
    loadYtApi(); // prefetch
  }

  _bindEvents() {
    this.opener.addEventListener('click', () => this.openModal());

    this.closeBtns.forEach(btn => btn.addEventListener('click', () => this.closeModal()));

    this.btnsPrev.forEach(btn => btn.addEventListener('click', (e) => { e.stopPropagation(); this.navigate(-1); }));
    this.btnsNext.forEach(btn => btn.addEventListener('click', (e) => { e.stopPropagation(); this.navigate(1); }));

    document.addEventListener('keydown', (e) => {
      if (!this.modal.hidden) {
        if (e.key === 'Escape') this.closeModal();
        if (e.key === 'ArrowLeft') this.navigate(-1);
        if (e.key === 'ArrowRight') this.navigate(1);
      }
    });
  }

  _initScrollBehavior() {
    // Show widget only after scrolling down 200px
    const toggleVisibility = () => {
      if (window.scrollY > 200) {
        this.opener.classList.add('is-visible');
      } else {
        this.opener.classList.remove('is-visible');
      }
    };

    // Initial check
    toggleVisibility();

    // Throttled scroll listener
    let scrollTimeout;
    window.addEventListener('scroll', () => {
      if (!scrollTimeout) {
        scrollTimeout = requestAnimationFrame(() => {
          toggleVisibility();
          scrollTimeout = null;
        });
      }
    }, { passive: true });
  }

  async openModal() {
    this.modal.hidden = false;
    document.body.style.overflow = 'hidden';

    this.updateUI();

    await loadYtApi();

    if (!this.player) {
      this.initPlayer();
    } else {
      this.playCurrent();
    }
  }

  closeModal() {
    this.modal.hidden = true;
    document.body.style.overflow = '';

    if (this.player && typeof this.player.pauseVideo === 'function') {
      this.player.pauseVideo();
    }
    this.stopProgress();
    this.resetProgressBars();
  }

  updateUI() {
    const story = this.stories[this.currentIndex];
    if (!story) return;

    // Make UI overlay visible
    this.uiOverlay.style.opacity = '1';

    // Texts
    this.titleEl.textContent = story.productTitle || '';
    this.descEl.textContent = story.productDesc || '';

    // Button
    if (story.buttonLink && story.buttonLink !== '') {
      this.actionLink.href = story.buttonLink;
      this.actionLink.textContent = story.buttonText || 'Comprar Agora';
      this.actionContainer.hidden = false;
    } else {
      this.actionContainer.hidden = true;
    }

    this.resetProgressBars();
  }

  initPlayer() {
    const story = this.stories[this.currentIndex];

    this.showLoader();

    this.player = new YT.Player(this.iframeContainerId, {
      videoId: story.videoId,
      playerVars: {
        autoplay: 1,
        controls: 0,
        rel: 0,
        modestbranding: 1,
        playsinline: 1,
        showinfo: 0,
        fs: 0,
        iv_load_policy: 3
      },
      events: {
        onReady: (e) => this.onPlayerReady(e),
        onStateChange: (e) => this.onPlayerStateChange(e)
      }
    });
  }

  onPlayerReady(event) {
    this.hideLoader();
    event.target.playVideo();
  }

  onPlayerStateChange(event) {
    // YT.PlayerState: -1 (unstarted), 0 (ended), 1 (playing), 2 (paused), 3 (buffering), 5 (video cued)
    if (event.data === YT.PlayerState.PLAYING) {
      this.hideLoader();
      this.isPlaying = true;
      this.startProgress();
    } else if (event.data === YT.PlayerState.ENDED) {
      this.navigate(1); // Auto-advance
    } else if (event.data === YT.PlayerState.BUFFERING) {
      this.showLoader();
    } else {
      this.isPlaying = false;
      this.stopProgress();
    }
  }

  _loadVideoById(videoId) {
    this.isPlaying = false;
    this.loader.hidden = false;

    if (this.poster) {
      this.poster.src = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    }

    if (this.player) {
      this.player.loadVideoById({ videoId: videoId });
    }
  }

  playCurrent() {
    const story = this.stories[this.currentIndex];
    this.showLoader();
    if (this.player && typeof this.player.loadVideoById === 'function') {
      this._loadVideoById(story.videoId);
    }
  }

  navigate(direction) {
    this.stopProgress();
    const target = this.currentIndex + direction;

    // Limits
    if (target < 0) {
      this.currentIndex = 0;
      this.playCurrent(); // replay first
      return;
    }

    if (target >= this.stories.length) {
      this.closeModal(); // close if it was the last one
      return;
    }

    this.currentIndex = target;
    this.updateUI();
    this.playCurrent();
  }

  // --- Loader ---
  showLoader() {
    this.loader.hidden = false;
  }

  hideLoader() {
    this.loader.hidden = true;
  }

  // --- Progress Bars Logic ---
  startProgress() {
    this.stopProgress();

    const duration = this.player.getDuration();
    if (!duration) return;

    const tick = () => {
      if (!this.isPlaying) return;

      const currentTime = this.player.getCurrentTime();
      const percent = (currentTime / duration) * 100;

      const bar = this.progressBars[this.currentIndex];
      if (bar) {
        bar.style.width = Math.min(percent, 100) + '%';
      }

      this.progressReq = requestAnimationFrame(tick);
    };

    this.progressReq = requestAnimationFrame(tick);
  }

  stopProgress() {
    if (this.progressReq) cancelAnimationFrame(this.progressReq);
  }

  resetProgressBars() {
    this.progressBars.forEach((bar, index) => {
      // Past stories are full
      if (index < this.currentIndex) {
        bar.style.width = '100%';
      }
      // Future stories are empty
      else if (index > this.currentIndex) {
        bar.style.width = '0%';
      }
      // Current story depends on playback, start at 0
      else {
        bar.style.width = '0%';
      }
    });
  }
}



/* — — — INIT — — — */
document.addEventListener('DOMContentLoaded', () => {
  const cartDrawer = new CartDrawer();
  window.cartDrawer = cartDrawer;
  window.cartDrawer = cartDrawer;
  new MobileMenu();
  initHeaderScroll();
  initScrollReveal();
  initProductGallery();
  initQuickAdd();

  initCartOpenEvent(cartDrawer);


  // Sliders & Interactive Sections
  $$('[data-hero-slider]').forEach(el => new HeroSlider(el));
  $$('.collection-slider').forEach(el => new CollectionSlider(el));
  $$('.custom-floating-video-section').forEach(el => new FloatingVideo(el));

  // Product forms
  $$('form[data-product-form]').forEach((form) => {
    new ProductForm(form);
  });

  // Variant selects
  $$('[data-variant-selects]').forEach((el) => new VariantSelects(el));

  // Render initial cart count
  CartAPI.getCart().then((cart) => {
    $$('.cart-count').forEach((el) => {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? 'flex' : 'none';
    });
  });
});


/* ——— WISHLIST MANAGER ——— */
class WishlistManager {
  constructor() {
    this.key = 'lumina_wishlist';
    this.items = this._load();
    this._bindEvents();
    this._updateButtons();
    // Dispatch initial event to sync pages that might be waiting
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('lumina:wishlist-updated', { detail: { items: this.items } }));
    }, 100);
  }

  _load() {
    try {
      const stored = localStorage.getItem(this.key);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Wishlist load error:', e);
      return [];
    }
  }

  _save() {
    try {
      localStorage.setItem(this.key, JSON.stringify(this.items));
      document.dispatchEvent(new CustomEvent('lumina:wishlist-updated', { detail: { items: this.items } }));
    } catch (e) {
      console.error('Wishlist save error:', e);
    }
  }

  _bindEvents() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wishlist-button]');
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();

      const handle = btn.dataset.productHandle;
      if (!handle) return;

      this.toggle(handle);
    });

    document.addEventListener('lumina:wishlist-updated', () => this._updateButtons());
  }

  toggle(handle) {
    const index = this.items.indexOf(handle);
    if (index > -1) {
      this.items.splice(index, 1);
      showToast('Removido dos favoritos', 'default');
    } else {
      this.items.push(handle);
      showToast('Adicionado aos favoritos ❤️', 'success');
    }
    this._save();
  }

  has(handle) {
    return this.items.indexOf(handle) > -1;
  }

  _updateButtons() {
    const btns = document.querySelectorAll('[data-wishlist-button]');
    btns.forEach(btn => {
      const handle = btn.dataset.productHandle;
      const isActive = this.has(handle);
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', isActive);
    });

    // Atualiza contadores no header
    const count = this.items.length;
    const countBadges = document.querySelectorAll('.wishlist-count');
    countBadges.forEach(badge => {
      badge.textContent = count;
      badge.style.display = count > 0 ? 'flex' : 'none';
    });

    // Atualiza contadores de texto (ex: no menu lateral)
    const countTexts = document.querySelectorAll('.wishlist-count-text');
    countTexts.forEach(el => {
      el.textContent = `(${count})`;
    });
  }
}

// Inicialização Global
document.addEventListener('DOMContentLoaded', () => {
  window.Wishlist = new WishlistManager();
});
